import { useMemo } from 'react';
import { useParams } from 'react-router';

// Characters that `decodeURI` leaves percent-encoded.
const reservedCharacters = /[;/?:@&=+$,#]/g;

/**
 * Route params with the react-router v5 contract this app was built on:
 *
 * - Values keep URI-reserved characters percent-encoded (v5's history ran
 *   `decodeURI` on the path; v6+ fully decodes params). Pages call
 *   `decodeURIComponent` on params and re-insert them into URLs, so a name
 *   containing `/` or `#` must stay encoded here.
 * - Params are typed as present. Components using this are only rendered
 *   under routes that define them (see `types/routes.ts`).
 */
export function useRouteParams<
  TParams extends { [K in keyof TParams]?: string },
>(): TParams {
  const params = useParams();

  return useMemo(
    () =>
      Object.fromEntries(
        Object.entries(params).map(([key, value]) => [
          key,
          value?.replace(reservedCharacters, encodeURIComponent),
        ]),
      ) as unknown as TParams,
    [params],
  );
}
